import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * MVP Keyboard Shortcuts Hook
 *
 * Provides essential keyboard shortcuts for navigation and common actions.
 *
 * Shortcuts:
 * - G then D: Go to Dashboard
 * - G then L: Go to Leads
 * - G then O: Go to Opportunities
 * - G then A: Go to Accounts
 * - G then T: Go to Tasks
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate()

  useEffect(() => {
    let gPressed = false
    let gTimer: NodeJS.Timeout | null = null

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input, textarea, or contenteditable
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      // Cmd/Ctrl + K is handled by the global command palette (CommandPalette).

      // G key shortcuts (Gmail-style navigation)
      if (event.key === 'g' && !gPressed) {
        event.preventDefault()
        gPressed = true

        // Reset after 1 second
        gTimer = setTimeout(() => {
          gPressed = false
        }, 1000)

        return
      }

      if (gPressed) {
        event.preventDefault()

        if (gTimer) {
          clearTimeout(gTimer)
          gTimer = null
        }

        gPressed = false

        switch (event.key) {
          case 'd':
            navigate('/')
            break
          case 'l':
            navigate('/leads')
            break
          case 'o':
            navigate('/opportunities')
            break
          case 'a':
            navigate('/accounts')
            break
          case 't':
            navigate('/tasks')
            break
          case 'c':
            navigate('/contacts')
            break
          default:
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (gTimer) {
        clearTimeout(gTimer)
      }
    }
  }, [navigate])
}
